/**
 * Quiz Buzz audio. Click on pick, ping on correct reveal, buzz on
 * wrong, countdown tick last 5s, podium fanfare. Same Web Audio API
 * synth pattern as the other games — no external samples.
 */

const LS_MUTE_KEY = 'quiz:mute';
const MASTER_GAIN = 0.18;

let ctx: AudioContext | null = null;
let audioPaused = false;
export function setAudioPaused(next: boolean): void {
  audioPaused = next;
  if (master) master.gain.value = muted || audioPaused ? 0 : MASTER_GAIN;
}

let master: GainNode | null = null;
let muted = ((): boolean => {
  try { return localStorage.getItem(LS_MUTE_KEY) === '1'; }
  catch { return false; }
})();

export function isMuted(): boolean { return muted; }
export function setMuted(next: boolean): void {
  muted = next;
  try { localStorage.setItem(LS_MUTE_KEY, next ? '1' : '0'); }
  catch { /* ignore */ }
  if (master) master.gain.value = muted || audioPaused ? 0 : MASTER_GAIN;
}
export function toggleMuted(): boolean { setMuted(!muted); return muted; }

export function initAudio(): void {
  if (ctx) { void ctx.resume().catch(() => {}); return; }
  try {
    const C = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    ctx = new C();
    master = ctx.createGain();
    master.gain.value = muted || audioPaused ? 0 : MASTER_GAIN;
    master.connect(ctx.destination);
    startMusic();
  } catch { ctx = null; master = null; }
}

function tone(opts: {
  freq: number; duration: number;
  type?: OscillatorType; attack?: number; release?: number;
  gain?: number; sweepTo?: number;
}): void {
  if (!ctx || !master) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = opts.type ?? 'sine';
  osc.frequency.value = opts.freq;
  if (opts.sweepTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.sweepTo), now + opts.duration);
  const g = ctx.createGain();
  const attack = opts.attack ?? 0.005;
  const peak = opts.gain ?? 0.5;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(peak, now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + opts.duration);
  osc.connect(g).connect(master);
  osc.start(now);
  osc.stop(now + opts.duration + 0.05);
}

// ── Quiz SFX ──────────────────────────────────────────────────────

export function sfxClick(): void {
  tone({ freq: 880, duration: 0.05, type: 'square', gain: 0.18, release: 0.01 });
}

export function sfxCorrect(): void {
  // Two-note rise. Major third.
  tone({ freq: 660, duration: 0.18, type: 'triangle', gain: 0.30, release: 0.06 });
  setTimeout(() => tone({ freq: 880, duration: 0.32, type: 'triangle', gain: 0.32, release: 0.15 }), 110);
}

export function sfxWrong(): void {
  // Detuned buzz.
  tone({ freq: 196, duration: 0.40, type: 'square', gain: 0.22, release: 0.05 });
  tone({ freq: 207, duration: 0.40, type: 'square', gain: 0.20, release: 0.05 });
}

export function sfxTick(): void {
  tone({ freq: 1000, duration: 0.06, type: 'triangle', gain: 0.20, release: 0.02 });
}

export function sfxReveal(): void {
  // Whoosh — short sweep up.
  tone({ freq: 200, sweepTo: 1200, duration: 0.18, type: 'sawtooth', gain: 0.20, release: 0.04 });
}

export function sfxRoundStart(): void {
  // Drum roll-ish — two quick ticks then a chord.
  tone({ freq: 440, duration: 0.10, type: 'triangle', gain: 0.25 });
  setTimeout(() => tone({ freq: 554, duration: 0.10, type: 'triangle', gain: 0.25 }), 100);
  setTimeout(() => tone({ freq: 659, duration: 0.40, type: 'triangle', gain: 0.30, release: 0.18 }), 200);
}

export function sfxFinal(): void {
  // Bigger fanfare for the end-of-game podium.
  const root = 392;
  for (const [note, delay] of [[1, 0], [1.25, 0.10], [1.5, 0.20], [2, 0.32], [1.5, 0.55], [2, 0.65], [2.5, 0.80]] as const) {
    setTimeout(() => tone({ freq: root * note, duration: 0.55, type: 'triangle', gain: 0.30, release: 0.22 }), delay * 1000);
  }
}

// ── Background music — bouncy quiz-show major arpeggio ───────────

interface MusicNote { freq: number; duration: number; type: OscillatorType; gain: number; }
const QUIZ_PATTERN: MusicNote[] = [
  { freq: 261.6, duration: 0.20, type: 'triangle', gain: 0.30 },
  { freq: 329.6, duration: 0.20, type: 'triangle', gain: 0.30 },
  { freq: 392.0, duration: 0.20, type: 'triangle', gain: 0.30 },
  { freq: 523.3, duration: 0.40, type: 'triangle', gain: 0.36 },
  { freq: 392.0, duration: 0.20, type: 'triangle', gain: 0.28 },
  { freq: 329.6, duration: 0.20, type: 'triangle', gain: 0.28 },
  { freq: 293.7, duration: 0.20, type: 'triangle', gain: 0.30 },
  { freq: 349.2, duration: 0.20, type: 'triangle', gain: 0.30 },
  { freq: 440.0, duration: 0.20, type: 'triangle', gain: 0.30 },
  { freq: 587.3, duration: 0.40, type: 'triangle', gain: 0.36 },
  { freq: 440.0, duration: 0.20, type: 'triangle', gain: 0.28 },
  { freq: 349.2, duration: 0.20, type: 'triangle', gain: 0.28 },
];
const MUSIC_GAIN = 0.06;
let musicBus: GainNode | null = null;
let musicIx = 0, musicNextStart = 0, musicHandle: number | null = null;

function scheduleMusicNote(note: MusicNote, when: number): void {
  if (!ctx || !musicBus) return;
  const osc = ctx.createOscillator();
  osc.type = note.type;
  osc.frequency.value = note.freq;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(note.gain * MUSIC_GAIN, when + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, when + note.duration);
  osc.connect(g).connect(musicBus);
  osc.start(when);
  osc.stop(when + note.duration + 0.05);
}
function musicTick(): void {
  if (!ctx || !musicBus) return;
  const lookahead = 0.3;
  while (musicNextStart < ctx.currentTime + lookahead) {
    const note = QUIZ_PATTERN[musicIx % QUIZ_PATTERN.length]!;
    scheduleMusicNote(note, musicNextStart);
    musicNextStart += note.duration;
    musicIx += 1;
  }
  musicHandle = window.setTimeout(musicTick, 100);
}
export function startMusic(): void {
  if (!ctx || !master || musicBus) return;
  const now = ctx.currentTime;
  musicBus = ctx.createGain();
  // Faster swell for the quiz — it's the most "show start" of the
  // three games, so 2.5s gain ramp + 3s filter open.
  musicBus.gain.setValueAtTime(0.0001, now);
  musicBus.gain.exponentialRampToValueAtTime(1.0, now + 2.5);
  const introFilter = ctx.createBiquadFilter();
  introFilter.type = 'lowpass';
  introFilter.frequency.setValueAtTime(600, now);
  introFilter.frequency.exponentialRampToValueAtTime(8000, now + 3);
  musicBus.connect(introFilter).connect(master);
  musicIx = 0;
  musicNextStart = now + 0.1;
  musicTick();
}
export function stopMusic(): void {
  if (musicHandle !== null) { clearTimeout(musicHandle); musicHandle = null; }
  if (musicBus) { try { musicBus.disconnect(); } catch { /* ignore */ } }
  musicBus = null;
}
